/**
 * Oct 2026 — "Image Assets" view for a Bidso SKU or Buyer SKU.
 *   • SKU asset rows (`sku_assets`): Drive links per asset type, cleared via
 *     status=INACTIVE (no delete).
 *   • Buyer level also shows the parent Bidso SKU's rows (context only) and
 *     the BOM's LB / PM / BS raw-material links (category_data.asset_link),
 *     set through PUT /raw-materials/{rm_id}/asset-link.
 * Links only — no file upload.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RMSearchSelect } from "@/components/bill/RMSearchSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ASSET_TYPE_LABELS = {
  product_render: "Product Render",
  product_actual_image: "Product Actual Image",
  product_catalog_image: "Product Catalog Image",
  die_line_diagram: "Die-line Diagram",
  generic: "Generic",
  video: "Video",
  hang_tags: "Hang Tags",
  warranty_card: "Warranty Card",
};
const SHARED_TYPES = Object.keys(ASSET_TYPE_LABELS).slice(0, 6);
// RM-backed entry choices → the RM category the picker is filtered to.
const RM_KINDS = [
  { key: "LB", label: "MRP Label (LB)" },
  { key: "PM", label: "Primary Box / Master Carton (PM)" },
  { key: "BS", label: "Brand Asset — stickers / Product Manual (BS)" },
];

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d ? JSON.stringify(d) : e.message;
};
const splitLinks = (s) => String(s || "").split(/\r?\n|\r/).map((x) => x.trim()).filter(Boolean);

const LinkList = ({ urls, testId }) => (
  <div className="space-y-0.5" data-testid={testId}>
    {urls.map((u) => (
      <a key={u} href={u} target="_blank" rel="noreferrer" className="block text-xs text-blue-700 underline break-all">{u}</a>
    ))}
  </div>
);

const TypeGroup = ({ group, readOnly, canUpdate, onClear, prefix }) => (
  <div className="border rounded p-2" data-testid={`${prefix}-type-${group.asset_type}`}>
    <div className="text-xs font-semibold text-zinc-700">{ASSET_TYPE_LABELS[group.asset_type]}</div>
    {group.is_empty ? (
      <div className="text-xs text-zinc-400 italic" data-testid={`${prefix}-empty-${group.asset_type}`}>No link yet</div>
    ) : group.rows.map((r) => (
      <div key={r.id} className="flex items-start justify-between gap-2 mt-1" data-testid={`${prefix}-row-${r.id}`}>
        <div className="min-w-0">
          {r.label && <div className="text-xs text-zinc-600">{r.label}</div>}
          <LinkList urls={[r.drive_url]} testId={`${prefix}-link-${r.id}`} />
        </div>
        {!readOnly && canUpdate && (
          <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => onClear(r)} data-testid={`${prefix}-clear-${r.id}`}>Clear</Button>
        )}
      </div>
    ))}
  </div>
);

export default function SKUAssetsDialog({ open, onClose, level, skuId, canCreate, canUpdate, getHeaders }) {
  const authHeaders = getHeaders;
  const isBuyer = level === "BUYER";
  const [data, setData] = useState(null);
  const [rms, setRms] = useState([]);
  const [mode, setMode] = useState("sku");
  const [form, setForm] = useState({ asset_type: "", drive_url: "", label: "", rm_kind: "", rm_id: "", links: "" });
  const [saving, setSaving] = useState(false);
  const [editRm, setEditRm] = useState(null);

  const load = useCallback(async () => {
    if (!skuId) return;
    try {
      const url = isBuyer ? `${API}/sku-assets/buyer-sku/${skuId}` : `${API}/sku-assets?bidso_sku_id=${encodeURIComponent(skuId)}`;
      const res = await axios.get(url, { headers: authHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error(`Failed to load assets: ${errMsg(e)}`);
    }
  }, [skuId, isBuyer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    setData(null);
    setMode("sku");
    setForm({ asset_type: "", drive_url: "", label: "", rm_kind: "", rm_id: "", links: "" });
    load();
    if (isBuyer) {
      // Full active RM list (not inward-bill-limited); category filter applied below.
      axios.get(`${API}/raw-materials?mode=list`, { headers: authHeaders() })
        .then((r) => setRms(Array.isArray(r.data) ? r.data : []))
        .catch(() => setRms([]));
    }
  }, [open, load, isBuyer]);

  const types = isBuyer ? Object.keys(ASSET_TYPE_LABELS) : SHARED_TYPES;
  const bomIds = useMemo(() => new Set(data?.bom_rm_ids || []), [data]);
  const pickerItems = useMemo(() => {
    const list = rms.filter((r) => (r.category || "").toUpperCase() === form.rm_kind);
    return [...list.filter((r) => bomIds.has(r.rm_id)), ...list.filter((r) => !bomIds.has(r.rm_id))]
      .map((r) => ({ ...r, description: `${r.description || r.name || ""}${bomIds.has(r.rm_id) ? " · on BOM" : ""}` }));
  }, [rms, form.rm_kind, bomIds]);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const clearRow = async (row) => {
    try {
      await axios.put(`${API}/sku-assets/${row.id}`, { status: "INACTIVE" }, { headers: authHeaders() });
      toast.success("Link cleared");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const currentRmLink = async (rmId) => {
    const hit = (data?.rm_links || []).find((r) => r.rm_id === rmId);
    if (hit) return hit.asset_link || "";
    const res = await axios.get(`${API}/raw-materials?search=${encodeURIComponent(rmId)}`, { headers: authHeaders() });
    const doc = (res.data || []).find((r) => r.rm_id === rmId);
    return doc?.category_data?.asset_link || "";
  };

  const saveRmLink = async (rmId, value) => {
    await axios.put(`${API}/raw-materials/${rmId}/asset-link`, { asset_link: value }, { headers: authHeaders() });
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      if (mode === "sku") {
        if (!form.asset_type || !form.drive_url.trim()) throw new Error("Pick an asset type and enter a link");
        await axios.post(`${API}/sku-assets`, {
          sku_level: isBuyer ? "BUYER_SKU" : "BIDSO_SKU",
          ...(isBuyer ? { buyer_sku_id: skuId } : { bidso_sku_id: skuId }),
          asset_type: form.asset_type, drive_url: form.drive_url.trim(), label: form.label,
        }, { headers: authHeaders() });
      } else {
        if (!form.rm_id) throw new Error("Pick an existing raw material first");
        const added = splitLinks(form.links);
        if (!added.length) throw new Error("Enter at least one link");
        const merged = [...new Set([...splitLinks(await currentRmLink(form.rm_id)), ...added])];
        await saveRmLink(form.rm_id, merged.join("\n"));
      }
      toast.success("Asset link saved");
      setForm({ asset_type: "", drive_url: "", label: "", rm_kind: form.rm_kind, rm_id: "", links: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const saveEditRm = async () => {
    try {
      await saveRmLink(editRm.rm_id, editRm.value);
      toast.success(`${editRm.rm_id} links updated`);
      setEditRm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ownGroups = isBuyer ? data?.own : data?.assets;
  const canAddSku = canCreate;
  const canAddRm = isBuyer && canUpdate;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="sku-assets-dialog">
        <DialogHeader>
          <DialogTitle>Image Assets — {skuId} <span className="text-xs text-zinc-500">({isBuyer ? "Buyer SKU" : "Bidso SKU"})</span></DialogTitle>
        </DialogHeader>
        {!data ? <div className="text-sm text-zinc-500">Loading…</div> : (
          <div className="space-y-4">
            <section>
              <h3 className="text-sm font-semibold mb-2">{isBuyer ? "This Buyer SKU's assets" : "Bidso SKU assets"}</h3>
              <div className="grid grid-cols-2 gap-2" data-testid="sku-assets-own">
                {(ownGroups || []).map((g) => (
                  <TypeGroup key={g.asset_type} group={g} canUpdate={canUpdate} onClear={clearRow} prefix="own" />
                ))}
              </div>
            </section>

            {isBuyer && (
              <section>
                <h3 className="text-sm font-semibold mb-1">From parent Bidso SKU {data.bidso_sku_id} <span className="text-xs font-normal text-zinc-500">(context only — not this Buyer SKU's assets)</span></h3>
                <div className="grid grid-cols-2 gap-2 opacity-90" data-testid="sku-assets-parent">
                  {data.parent_bidso.map((g) => (
                    <TypeGroup key={g.asset_type} group={g} readOnly prefix="parent" />
                  ))}
                </div>
              </section>
            )}

            {isBuyer && (
              <section>
                <h3 className="text-sm font-semibold mb-1">Raw-material links on this BOM (LB / PM / BS)</h3>
                {data.rm_links.length === 0 ? (
                  <div className="text-xs text-zinc-500 italic" data-testid="sku-assets-rm-none">{data.rm_links_note}</div>
                ) : (
                  <div className="space-y-2" data-testid="sku-assets-rm-links">
                    {data.rm_links.map((r) => (
                      <div key={r.rm_id} className="border rounded p-2" data-testid={`rm-link-${r.rm_id}`}>
                        <div className="flex justify-between gap-2">
                          <div className="text-xs"><span className="font-mono">{r.rm_id}</span> · {r.category_label} · {r.description}</div>
                          {canUpdate && (
                            <Button size="sm" variant="ghost" className="h-6 text-xs" data-testid={`rm-link-edit-${r.rm_id}`}
                              onClick={() => setEditRm({ rm_id: r.rm_id, value: r.urls.join("\n") })}>Edit links</Button>
                          )}
                        </div>
                        {r.is_empty
                          ? <div className="text-xs text-zinc-400 italic" data-testid={`rm-link-empty-${r.rm_id}`}>No link set</div>
                          : <LinkList urls={r.urls} testId={`rm-link-urls-${r.rm_id}`} />}
                        {editRm?.rm_id === r.rm_id && (
                          <div className="mt-2 space-y-1">
                            <Textarea rows={3} value={editRm.value} onChange={(e) => setEditRm({ ...editRm, value: e.target.value })}
                              placeholder="One https link per line" data-testid={`rm-link-edit-input-${r.rm_id}`} />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={saveEditRm} data-testid={`rm-link-edit-save-${r.rm_id}`}>Save</Button>
                              <Button size="sm" variant="outline" onClick={() => setEditRm(null)} data-testid={`rm-link-edit-cancel-${r.rm_id}`}>Cancel</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {(canAddSku || canAddRm) && (
              <section className="border-t pt-3 space-y-2" data-testid="sku-assets-add">
                <h3 className="text-sm font-semibold">Add asset link</h3>
                {isBuyer && canAddSku && canAddRm && (
                  <div className="flex gap-2">
                    <Button size="sm" variant={mode === "sku" ? "default" : "outline"} onClick={() => setMode("sku")} data-testid="add-mode-sku">Image / video / diagram</Button>
                    <Button size="sm" variant={mode === "rm" ? "default" : "outline"} onClick={() => setMode("rm")} data-testid="add-mode-rm">Label / box / brand asset (raw material)</Button>
                  </div>
                )}
                {(mode === "sku" && canAddSku) || (!canAddRm && canAddSku) ? (
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Asset type</Label>
                      <Select value={form.asset_type} onValueChange={setF("asset_type")}>
                        <SelectTrigger data-testid="add-asset-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          {types.map((t) => <SelectItem key={t} value={t} data-testid={`add-asset-type-${t}`}>{ASSET_TYPE_LABELS[t]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Drive link</Label><Input value={form.drive_url} onChange={setF("drive_url")} placeholder="https://drive.google.com/…" data-testid="add-asset-url" /></div>
                    <div><Label className="text-xs">Label (optional)</Label><Input value={form.label} onChange={setF("label")} data-testid="add-asset-label" /></div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Kind</Label>
                        <Select value={form.rm_kind} onValueChange={(v) => setForm((f) => ({ ...f, rm_kind: v, rm_id: "" }))}>
                          <SelectTrigger data-testid="add-rm-kind"><SelectValue placeholder="Select kind" /></SelectTrigger>
                          <SelectContent>
                            {RM_KINDS.map((k) => <SelectItem key={k.key} value={k.key} data-testid={`add-rm-kind-${k.key}`}>{k.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Raw material (BOM items first)</Label>
                        {form.rm_kind ? (
                          <RMSearchSelect value={form.rm_id} onChange={setF("rm_id")} items={pickerItems}
                            placeholder={`Search ${form.rm_kind} by ID or name…`} emptyHint="No matching raw material" testIdPrefix="add-rm" />
                        ) : <div className="text-xs text-zinc-400 pt-2">Pick a kind first</div>}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Link(s) — one https link per line, added to the material's existing links</Label>
                      <Textarea rows={2} value={form.links} onChange={setF("links")} data-testid="add-rm-links" />
                    </div>
                    <div className="text-xs text-zinc-500" data-testid="add-rm-request-hint">
                      Material not listed? Raise it through the existing <Link className="underline" to="/demand-hub">RM request flow</Link> — this screen never creates raw materials.
                    </div>
                  </div>
                )}
                <Button size="sm" onClick={handleAdd} disabled={saving} data-testid="add-asset-save">{saving ? "Saving…" : "Save link"}</Button>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
